import * as React from "react";
import { type QueryClient, queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { type IAiAssistArgs, getAiAssist } from "@/api";
import { withAuth } from "@/state/withAuth";
import { AI_LANGUAGES, type IContentType, type ILanguage } from "@/types";

const MAX_CONCURRENT_PREFETCH = 3;
const AI_PREFETCH_DEBOUNCE_MS = 1000;
const prefetchQueue: VoidFunction[] = [];
let activePrefetchCount = 0;

function processPrefetchQueue() {
    while (prefetchQueue.length > 0 && activePrefetchCount < MAX_CONCURRENT_PREFETCH) {
        const nextPrefetch = prefetchQueue.shift();
        if (nextPrefetch) {
            activePrefetchCount++;
            nextPrefetch();
        }
    }
}

function authGetAiAssist(params: IAiAssistArgs) {
    return withAuth(() => getAiAssist(params));
}

function aiQueryOptions(params: IAiAssistArgs) {
    const skip = !AI_LANGUAGES.includes(params.language);
    return queryOptions({
        queryKey: [
            "ai-assist",
            params.contentType,
            params.id,
            params.language,
            params.text,
            { version: params.version },
        ],
        queryFn: () => authGetAiAssist(params),
        enabled: !!params.text && !skip,
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: Number.POSITIVE_INFINITY,
    });
}

export function useAiAssist(params: IAiAssistArgs) {
    return useQuery(aiQueryOptions(params));
}

export function prefetchAiAssist(params: IAiAssistArgs, queryClient: QueryClient) {
    const queryKey = [
        "ai-assist",
        params.contentType,
        params.id,
        params.language,
        params.text,
        { version: params.version },
    ];

    // If data is already in cache, don't prefetch
    if (queryClient.getQueryData(queryKey)) {
        return;
    }

    const prefetchFn = () => {
        queryClient.prefetchQuery(aiQueryOptions(params)).finally(() => {
            activePrefetchCount--;
            processPrefetchQueue();
        });
    };

    prefetchQueue.push(prefetchFn);
    processPrefetchQueue();
}

interface IUsePrefetchAiAssistArgs {
    id: string;
    contentType: IContentType;
    languages: ILanguage[];
    text: string;
    version: string;
}

function aiLanguageKey(languages: ILanguage[]): string {
    return languages.filter((lang) => AI_LANGUAGES.includes(lang)).join(",");
}

export function usePrefetchAiAssist({ id, contentType, languages, text, version }: IUsePrefetchAiAssistArgs) {
    const queryClient = useQueryClient();
    const languagesKey = aiLanguageKey(languages);

    React.useEffect(() => {
        if (!text || !languagesKey) {
            return;
        }

        const timer = setTimeout(() => {
            const aiLanguages = languagesKey.split(",") as ILanguage[];
            aiLanguages.forEach((language) => {
                prefetchAiAssist(
                    {
                        id,
                        contentType,
                        language,
                        text,
                        version,
                    },
                    queryClient,
                );
            });
        }, AI_PREFETCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
        };
    }, [contentType, id, languagesKey, queryClient, text, version]);
}
